import { useFormikContext } from 'formik';
import Papa from 'papaparse';
import React from 'react';
import { toast } from 'sonner';

import { ButtonBase, Tooltip } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';

import Button from 'creative-components/CustomButtons/Button';

import TrashIcon from 'components/CustomIcons/TrashIcon';
import { useLoadingIndicatorContext } from 'components/LoadingIndicator/LoadingIndicatorProvider';

import { checkCampaignAddOnProperties } from 'utils/api';
import { showAPIErrorAlert } from 'utils/lib';

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: '800px',
  },
  title: {
    margin: '30px 0 6px',
    fontWeight: 'bold',
  },
  subTitle: {
    fontSize: '14px',
    color: theme.palette.gray.main,
    fontWeight: 500,
  },
  container: {
    marginTop: '30px',
    display: 'inline-block',
    '& > span': {
      display: 'inline-block',
      marginLeft: '50px',
      fontWeight: 500,
      color: theme.palette.darkGray.main,
      '& > button': {
        marginLeft: '10px',
      },
    },
  },
}));

const exampleCsvFileName = 'campaign-add-on-properties-example.csv';
const addOnPropertiesCountName = 'Add On Properties';

const upsertAddOnPropertiesCount = (counts, count) => [
  ...counts.filter(({ name }) => name !== addOnPropertiesCountName),
  {
    name: addOnPropertiesCountName,
    count,
  },
];

const AddOnPropertiesInput = ({
  apnAvailableCounts, setApnAvailableCounts, allApnAvailableCounts, setAllApnAvailableCounts,
  highPropensityApnAvailableCounts, setHighPropensityApnAvailableCounts, apnSelectedCounts, setApnSelectedCounts,
  setApnsTotalSelectedCount, apnsTotalInAreaCount, setApnsTotalInAreaCount,
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const { showLoadingIndicatorModal, hideLoadingIndicatorModal } = useLoadingIndicatorContext();
  const {
    values, touched, errors, setFieldValue, setTouched, isValid,
  } = useFormikContext();

  const checkAndSetAddOnProperties = async (propertyData, showSuccessAlert) => {
    showLoadingIndicatorModal();

    try {
      const addOnPropertiesJsonString = JSON.stringify(propertyData);

      const {
        nonOverlappingAddOnPropertyData,
        duplicateApns = [],
        duplicateRowsRemoved = duplicateApns.length,
      } = await checkCampaignAddOnProperties(addOnPropertiesJsonString);
      const addOnPropertiesCount = nonOverlappingAddOnPropertyData.length;
      const deduplicatedPropertyCount = propertyData.length - duplicateRowsRemoved;

      setFieldValue('addOnPropertiesJsonString', JSON.stringify(nonOverlappingAddOnPropertyData), true);

      const newApnAvailableCounts = upsertAddOnPropertiesCount(apnAvailableCounts, addOnPropertiesCount);
      const newAllApnAvailableCounts = upsertAddOnPropertiesCount(allApnAvailableCounts, addOnPropertiesCount);
      const newHighPropensityApnAvailableCounts = upsertAddOnPropertiesCount(highPropensityApnAvailableCounts, addOnPropertiesCount);

      // If there's a previous count from add on properties, get that so we subtract it from the total in area count
      const previousAddOnCount = apnSelectedCounts.find(({ name }) => name === addOnPropertiesCountName)?.count ?? 0;

      const newApnSelectedCounts = upsertAddOnPropertiesCount(apnSelectedCounts, addOnPropertiesCount);

      setApnAvailableCounts(newApnAvailableCounts);
      setAllApnAvailableCounts(newAllApnAvailableCounts);
      setHighPropensityApnAvailableCounts(newHighPropensityApnAvailableCounts);
      setApnSelectedCounts(newApnSelectedCounts);
      setApnsTotalSelectedCount(newApnSelectedCounts.reduce((sum, { count }) => count + sum, 0));

      // Remove previous add on count. This is so if they upload different
      // CSVs, it only includes the count from the one uploaded just now
      setApnsTotalInAreaCount(apnsTotalInAreaCount + addOnPropertiesCount - previousAddOnCount);

      if (duplicateApns.length > 0) {
        toast.warning(
          `We removed ${duplicateRowsRemoved} duplicate ${duplicateRowsRemoved > 1 ? 'rows' : 'row'} from your CSV and continued the upload. Duplicate parcel ${duplicateApns.length > 1 ? 'numbers' : 'number'}: ${duplicateApns.join(', ')}.`,
          { duration: 60000 },
        );
      }

      if (deduplicatedPropertyCount !== nonOverlappingAddOnPropertyData.length) {
        const numTaken = deduplicatedPropertyCount - nonOverlappingAddOnPropertyData.length;
        toast.warning(
          `We found ${deduplicatedPropertyCount} unique APNs in your uploaded file, but only ${nonOverlappingAddOnPropertyData.length} are available because ${numTaken} ${numTaken > 1 ? 'are' : 'is'} being actively farmed by other agents.`,
          { duration: 60000 },
        );
      } else if (showSuccessAlert && duplicateApns.length === 0) {
        toast.success(`We found ${deduplicatedPropertyCount} unique extra APNs in your uploaded file.`, { duration: 15000 });
      }
    } catch (err) {
      console.error('error', err);
      if (err.response?.data?.error?.includes('ValidationError')) {
        toast.error('Please make sure your CSV follows the sample format.');
      } else {
        showAPIErrorAlert(err);
      }
    }

    hideLoadingIndicatorModal();
  };

  // If a draft is being loaded, set the apn available counts based off any existing addOnPropertiesJsonString
  React.useEffect(() => {
    (async () => {
      if (!values.addOnPropertiesJsonString) return;

      const propertyData = JSON.parse(values.addOnPropertiesJsonString);

      await checkAndSetAddOnProperties(propertyData, false);
    })();
  }, []);

  const addOnPropertiesCountArr = apnSelectedCounts.filter(({ name }) => name === addOnPropertiesCountName);

  const fileInput = React.createRef();

  const handleCsvChange = async (e) => {
    e.preventDefault();

    const file = e.target.files[0];
    if (!file) return;

    // Show a loading indicator during the upload
    showLoadingIndicatorModal();

    // Count the # of properties in the CSV
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      async complete(results) {
        // Basic parse errors
        if (results.errors.length > 0) {
          console.error(results.errors);
          toast.error('There was an error reading your CSV file.');
          hideLoadingIndicatorModal();
          return;
        }

        // Validate that we actually got some rows
        if (!results.data || results.data.length === 0) {
          toast.error('Your CSV appears to be empty.');
          hideLoadingIndicatorModal();
          return;
        }

        // Validate headers using meta.fields
        const headers = results.meta.fields ?? [];

        const missingRequired = ['Parcel Number', 'Zip Code'].filter((h) => !headers.includes(h));
        if (missingRequired.length > 0) {
          toast.error(
            `Missing required column${missingRequired.length > 1 ? 's' : ''}: ${missingRequired.join(', ')}.`,
          );
          hideLoadingIndicatorModal();
          return;
        }

        // Check that first data row actually has values for the required columns
        const firstRow = results.data[0];
        if (!firstRow['Parcel Number'] || !firstRow['Zip Code']) {
          toast.error('Please make sure your CSV file follows the example format.');
          hideLoadingIndicatorModal();
          return;
        }

        // Safely map data, knowing required headers are there
        const propertyData = results.data.map((row) => ({
          parcelNumber: row['Parcel Number'],
          zipCode: row['Zip Code'],
          firstName: row['First Name (Optional)'] ? row['First Name (Optional)'] : undefined,
          lastName: row['Last Name (Optional)'] ? row['Last Name (Optional)'] : undefined,
          email: row['Email Address (Optional)'] ? row['Email Address (Optional)'] : undefined,
          phoneNumber: row['Phone Number (Optional)'] ? row['Phone Number (Optional)'] : undefined,
        }));

        await checkAndSetAddOnProperties(propertyData, true);
      },
    });
  };

  const handleRemove = async () => {
    fileInput.current.value = null; // Allows the same file to be selected again
    setFieldValue('addOnPropertiesJsonString', null, true);

    const previousAddOnCount = apnSelectedCounts.find(({ name }) => name === addOnPropertiesCountName)?.count ?? 0;
    const newApnAvailableCounts = apnAvailableCounts.filter(({ name }) => name !== addOnPropertiesCountName);
    const newAllApnAvailableCounts = allApnAvailableCounts.filter(({ name }) => name !== addOnPropertiesCountName);
    const newHighPropensityApnAvailableCounts = highPropensityApnAvailableCounts.filter(({ name }) => name !== addOnPropertiesCountName);
    const newApnSelectedCounts = apnSelectedCounts.filter(({ name }) => name !== addOnPropertiesCountName);

    setApnAvailableCounts(newApnAvailableCounts);
    setAllApnAvailableCounts(newAllApnAvailableCounts);
    setHighPropensityApnAvailableCounts(newHighPropensityApnAvailableCounts);
    setApnSelectedCounts(newApnSelectedCounts);
    setApnsTotalSelectedCount(newApnSelectedCounts.reduce((sum, { count }) => count + sum, 0));
    setApnsTotalInAreaCount(apnsTotalInAreaCount - previousAddOnCount);
  };

  return (
    <div className={classes.root}>
      <h4 className={classes.title}>
        Add extra APNs to your campaign's farm by uploading a CSV file
      </h4>
      <h5 className={classes.subTitle}>
        We will verify the APNs/parcel numbers you upload and check for duplicates in your selected farm area when starting your campaign. If any duplicates are found, your actual number of APNs will be lower than estimated.
        <br />
        <br />
        Your file must follow the
        <strong><a download target="_blank" rel="noreferrer" href={`/${exampleCsvFileName}`} style={{ color: theme.palette.primary.main }}> example format </a></strong>
        and all properties must be in the same state. You may optionally provide names, email addresses, and phone numbers to override the property data we pull.
      </h5>
      <input
        type="file"
        accept="text/csv"
        onChange={handleCsvChange}
        ref={fileInput}
        style={{ display: 'none' }}
      />
      <div className={classes.container}>
        <Button
          round
          color="orange"
          onClick={() => {
            if (fileInput.current) fileInput.current.value = null; // Reset so same filename triggers onChange
            fileInput.current.click();
          }}
        >
          <InsertDriveFileIcon />
          {' '}
          Upload CSV File
        </Button>
        {addOnPropertiesCountArr.length > 0 && (
          <span>
            {`${addOnPropertiesCountArr[0].count} Extra APNs`}
            <Tooltip
              title="Remove"
              placement="top"
            >
              <ButtonBase onClick={handleRemove}>
                <TrashIcon color={theme.palette.error.main} />
              </ButtonBase>
            </Tooltip>
          </span>
        )}
      </div>
    </div>
  );
};

export default AddOnPropertiesInput;
